import { VendorTaxonomyKey } from '@zola-helpers/client/dist/es/marketplace/vendorTaxonomyKeys';
import { getVendorType } from '@zola-helpers/client/dist/es/marketplace/vendorUtils';
import {
  UpdateAccountVendorRequest,
  UpdateAccountVendorsRequest,
  BudgetItemSummaryViewResponse,
  ResolveBookedVendorToBudgetConflictRequest,
} from '@zola/svc-marketplace-ts-types';
import * as toastsActions from '@zola/zola-ui/src/actions/toastsV2Actions';

import _values from 'lodash/values';

import { AppThunk } from '~/reducers';
import type {
  AccountVendorResultView,
  AccountVendorView,
  BudgetResolutionFormValues,
} from '~/types/responseTypes';
import { parseDate } from '~/util/dateUtils';

import ApiService from '../util/apiService';
import Logger, { StandardLogFn } from '../util/logger';
import { dollarsToCents } from '../util/priceConversion';
import {
  REQUESTING_BOOKED_VENDORS,
  RECEIVED_BOOKED_VENDORS,
  REQUESTING_CREATE_BOOKED_VENDOR,
  RECEIVED_CREATE_BOOKED_VENDOR,
  SENDING_REMOVE_VENDOR_FEEDBACK,
  SENT_REMOVE_VENDOR_FEEDBACK,
  SENDING_UNBOOK_VENDOR_CATEGORY,
  SENT_UNBOOK_VENDOR_CATEGORY,
  UPDATING_BOOKED_VENDORS,
  RequestingCreateBookedVendorAction,
  ReceivedCreateBookedVendorAction,
  ReceivedBookedVendorsAction,
  RequestingBookedVendorsAction,
  SendingRemoveVendorFeedbackAction,
  SentRemoveVendorFeedbackAction,
  SendingUnbookVendorCategoryAction,
  SentUnbookVendorCategoryAction,
} from './types/bookedVendorsActionTypes';

function requestingCreateBookedVendor(): RequestingCreateBookedVendorAction {
  return {
    type: REQUESTING_CREATE_BOOKED_VENDOR,
  };
}

function receivedCreateBookedVendor(response: AccountVendorView): ReceivedCreateBookedVendorAction {
  return {
    type: RECEIVED_CREATE_BOOKED_VENDOR,
    payload: response,
  };
}

export function addBookedVendorV2(vendorType: VendorTaxonomyKey): AppThunk<Promise<void>> {
  return (dispatch, getState) => {
    const state = getState();
    const { bookableVendors } = state.bookedVendors;
    const priority = bookableVendors.allIds.length + 1;
    const newBookable = {
      vendorType: getVendorType(vendorType),
      priority,
    };
    dispatch(requestingCreateBookedVendor());
    return ApiService.post(
      `/web-marketplace-api/v2/account/vendor?priority=${priority}`,
      newBookable
    )
      .then((json: AccountVendorView) => {
        dispatch(receivedCreateBookedVendor(json));
      })
      .catch((err) => {
        Logger.error('error loading booked vendors: ', err);
      });
  };
}

function requestingBookedVendors(): RequestingBookedVendorsAction {
  return {
    type: REQUESTING_BOOKED_VENDORS,
  };
}

export const receivedBookedVendors = (
  response: AccountVendorView[]
): ReceivedBookedVendorsAction => {
  return {
    type: RECEIVED_BOOKED_VENDORS,
    payload: response,
  };
};

export function requestBookedVendors(): AppThunk<Promise<void>> {
  return (dispatch) => {
    dispatch(requestingBookedVendors());

    return ApiService.post('/web-marketplace-api/v1/account/get-or-create-vendors')
      .then((json) => {
        dispatch(receivedBookedVendors(json));
      })
      .catch((err) => {
        Logger.error('error loading booked vendors: ', err);
      });
  };
}

function sendingRemoveVendorFeedback(): SendingRemoveVendorFeedbackAction {
  return {
    type: SENDING_REMOVE_VENDOR_FEEDBACK,
  };
}

function sentRemoveVendorFeedback(json: AccountVendorView): SentRemoveVendorFeedbackAction {
  return {
    type: SENT_REMOVE_VENDOR_FEEDBACK,
    payload: json,
  };
}

export function sendRemoveVendorFeedback(
  vendorUuid: string,
  formValues: Record<string, unknown> = {}
): AppThunk<Promise<void>> {
  return (dispatch) => {
    dispatch(sendingRemoveVendorFeedback());
    const url = `/web-marketplace-api/v1/account/vendor/${vendorUuid}/un-book`;
    /* filter out any form values that are not true */
    const truthyValues = Object.keys(formValues).filter((key) => formValues[key] && key);
    const body = {
      reasons: truthyValues,
    };
    return ApiService.post(url, body)
      .then((json: AccountVendorView) => {
        dispatch(sentRemoveVendorFeedback(json));
      })
      .catch((err) => {
        Logger.error('error sending remove vendor feedback: ', err);
      });
  };
}

function parsePrice(str: string) {
  const isDollarsAndCents = new RegExp(/^(\d+)\.(\d{2})$/);
  if (isDollarsAndCents.test(str)) {
    return parseFloat(str) * 100;
  }
  return dollarsToCents(str);
}

export interface BookVendorModalFormValues {
  vendorName: string;
  vendorType: string;
  city: string;
  state: string;
  email?: string;
  price?: string;
  eventDate?: string;
  referenceVendorId?: number;
  uuid?: string;
}

function sendingUnbookVendorCategory(): SendingUnbookVendorCategoryAction {
  return {
    type: SENDING_UNBOOK_VENDOR_CATEGORY,
  };
}

function sentUnbookVendorCategory(response: AccountVendorView): SentUnbookVendorCategoryAction {
  return {
    type: SENT_UNBOOK_VENDOR_CATEGORY,
    payload: response,
  };
}

export function unBookVendorCategory(vendor: AccountVendorView): AppThunk<Promise<void>> {
  return (dispatch) => {
    dispatch(sendingUnbookVendorCategory());
    const deleteBookingUrl = `/web-marketplace-api/v1/account/vendor/${vendor.uuid}`;
    // Note: return was missing here
    return ApiService.delete(deleteBookingUrl)
      .then((json: AccountVendorView) => {
        dispatch(sentUnbookVendorCategory(json));
        dispatch(
          toastsActions.positive({
            headline: `We've successfully removed ${
              vendor.vendorName || 'this category'
            } from your list of booked vendors!`,
            dismissText: 'Close',
            autoDismissInSeconds: 10,
          })
        );
        return dispatch(requestBookedVendors()); // refresh all to get vendorCards
      })
      .catch((error) => {
        Logger.error(error.message);
        dispatch(
          toastsActions.negative({
            headline: `We could not remove your vendor booking. Please try again.`,
            dismissText: 'Close',
            autoDismissInSeconds: 10,
          })
        );
      });
  };
}

function updatingBookedVendors() {
  // Need to update
  return {
    type: UPDATING_BOOKED_VENDORS,
    // payload: response, // Dont know if this is right
  };
}

/* update set of booked vendors (used during drag & drop) */
export function reorderBookedVendors(
  accountVendors: AccountVendorView[] = [],
  platformType: UpdateAccountVendorsRequest.PlatformTypeEnum
): AppThunk<Promise<void>> {
  return (dispatch) => {
    // api needs additional boolean to retain booked_at timestamp
    const updateRequest: UpdateAccountVendorRequest[] = accountVendors.map((accountVendor) => {
      const vendorType = getVendorType(
        accountVendor.vendorType
      ) as UpdateAccountVendorRequest.VendorTypeEnum;

      // remove nanoids and set to null, to indicate new entries
      let id = null;
      if (typeof accountVendor.id === 'number') {
        // eslint-disable-next-line prefer-destructuring
        id = accountVendor.id;
      }
      const { eventDate, referenceVendorId, vendorCard, vendorName, priceCents, booked } =
        accountVendor;
      return {
        id,
        referenceVendorRequest: vendorName
          ? {
              id: referenceVendorId ?? null,
              name: vendorName,
              websiteUrl: null,
              email: null,
              phone: null,
              address: null,
            }
          : null,
        vendorType,
        eventDate,
        referenceVendorId,
        facetKeys: null,
        userObjectId: null,
        storefrontUuid: vendorCard?.storefrontUuid ?? null,
        booked,
        syncWithBudgetToolEnabled: null,
        priceCents,
        platformType: null,
      };
    });

    const body: UpdateAccountVendorsRequest = {
      accountVendorRequests: updateRequest,
      platformType,
    };
    dispatch(updatingBookedVendors());
    return ApiService.put<AccountVendorView[], UpdateAccountVendorsRequest>(
      '/web-marketplace-api/v1/account/vendors',
      body
    )
      .then((response) => {
        dispatch(receivedBookedVendors(response));
      })
      .catch((error) => {
        Logger.error(error.message);
      });
  };
}

export function createOrUpdateBookedVendorV2(
  formValues: BookVendorModalFormValues,
  showBudgetResolutionModal: (response: AccountVendorResultView) => void,
  showBookedConfirmationModal: (vendorName: string, storefrontCoverId: string | null) => void
): AppThunk<Promise<AccountVendorResultView | void>> {
  return (dispatch, getState) => {
    const state = getState();
    const { bookableVendors } = state.bookedVendors;
    const {
      eventDate,
      referenceVendorId,
      price,
      vendorName,
      vendorType,
      city,
      state: stateProvince,
      email,
      uuid,
    } = formValues;
    const type = getVendorType(vendorType);

    // not sure what this is for?
    const firstBookableVendorOfType = _values(bookableVendors.byId).find(
      (i) => i.vendorType === type && !i.vendorName
    );
    const bookingUuid = firstBookableVendorOfType?.uuid;
    const body = {
      vendorType: type,
      booked: true,
      eventDate: eventDate ? parseDate(eventDate) : null,
      priceCents: price ? parsePrice(price) : null,
      uuid: bookingUuid || uuid,
      facetKeys: [],
      referenceVendorRequest: {
        name: vendorName,
        email,
        address: {
          city,
          stateProvince,
        },
      },
      id: referenceVendorId,
    };

    const createBooking =
      bookingUuid || uuid
        ? ApiService.put(`/web-marketplace-api/v2/account/vendor/${uuid || bookingUuid}`, body)
        : ApiService.post('/web-marketplace-api/v2/account/vendor?priority=0', body);

    return createBooking
      .then((response) => {
        const { manualResolutionNeeded } = response.budgetSyncResolution;
        if (manualResolutionNeeded) {
          showBudgetResolutionModal(response);
        } else {
          showBookedConfirmationModal(
            vendorName,
            response.accountVendor.vendorCard.storefrontCoverId
          );
        }
        dispatch(requestBookedVendors()).catch(() => null);
      })
      .catch((error: Error) => {
        StandardLogFn(error);
        dispatch(
          toastsActions.negative({
            headline: `We could not save your vendor booking. Please try again.`,
            dismissText: 'Close',
            autoDismissInSeconds: 10,
          })
        );
      });
  };
}

export function resolveBookedVendorConflicts(
  formValues: BudgetResolutionFormValues,
  priceCents?: number
): Promise<BudgetItemSummaryViewResponse> {
  const { budgetItemUuid, costCents, accountVendorUuid } = formValues;
  const maxCost = costCents === 0 ? Math.max(costCents, priceCents || 0) : costCents;
  const body: ResolveBookedVendorToBudgetConflictRequest = {
    budgetItemUuid: budgetItemUuid as string,
    costCents: maxCost,
    accountVendorUuid,
  };
  return ApiService.post<BudgetItemSummaryViewResponse, ResolveBookedVendorToBudgetConflictRequest>(
    `/web-marketplace-api/v2/account/vendor/resolve-sync-conflicts`,
    body
  );
}

export function addNewBudgetItem(
  vendorName: string,
  formValues: BudgetResolutionFormValues,
  accountId: number,
  vendorType: string,
  priceCents: number
) {
  const { accountVendorUuid } = formValues;
  const body = {
    accountId,
    accountVendorUuid,
    platformType: 'ALL',
    vendorType,
    vendorName,
    priceCents,
  };
  return ApiService.post(
    `/web-marketplace-api/v2/account/vendor/create-budget-item-from-vendor`,
    body
  );
}
